import {
  getOrganizationAvatar,
  getProjectInitial,
} from "@frontend-shared/utils";
import { memo } from "react";

import { cn } from "@/lib/utils";

interface IProjectAvatarProps {
  name: string;
  // NOTE: the organization the project belongs to, which is where the tint
  // comes from. Absent for the personal project, which belongs to none.
  organizationUuid?: string;
  organizationName?: string;
  className?: string;
}

// NOTE: NSAI-6258 — the tint and the ring, as fractions of the organization's
// hue. Low enough that a column of tiles reads as texture behind the project
// names rather than as a second row of badges competing with the organization
// above them.
const TINT_ALPHA = "1f";
const RING_ALPHA = "59";

/**
 * NOTE: NSAI-6258 — a project's mark. Deliberately NOT the organization badge
 * at a smaller size: it is a rounded square where an organization is a circle,
 * and one letter where an organization has two, so the two ranks of a list that
 * indents projects under their organization stay legible as two ranks.
 *
 * The colour is the ORGANIZATION's, tinted: the tile is that hue at ~12% with a
 * ~35% ring, and the letter takes the theme's own foreground rather than the
 * hue. Two things follow from that. A project reads as belonging to the
 * organization above it instead of introducing a colour of its own, which a
 * per-project hash would have done — turning the list into confetti and saying
 * nothing true, since a project's identity is its name. And the letter's
 * contrast is the theme's to guarantee, so this needs no per-theme variant and
 * no palette entry that happens to work on both surfaces.
 *
 * The personal project belongs to no organization, so it gets the same tile in
 * the muted foreground: still a mark, visibly not part of any organization.
 */
const ProjectAvatarComponent = ({
  name,
  organizationUuid,
  organizationName,
  className,
}: IProjectAvatarProps): JSX.Element => {
  const initial = getProjectInitial({ name });
  const organizationColor = organizationName
    ? getOrganizationAvatar({ uuid: organizationUuid, name: organizationName })
        .color
    : null;

  return (
    <span
      // NOTE: the project's name sits beside it, so the tile is decoration to a
      // screen reader rather than a second copy of the label.
      aria-hidden="true"
      data-testid="project-avatar"
      data-project-initial={initial}
      className={cn(
        "flex h-6 w-6 shrink-0 select-none items-center justify-center rounded-md",
        "border text-[11px] font-semibold uppercase leading-none text-foreground",
        // NOTE: the fallback for a project outside any organization — the same
        // tile, drawn in the theme's own neutrals.
        !organizationColor &&
          "border-border bg-muted text-foreground-secondary",
        className,
      )}
      style={
        organizationColor
          ? {
              backgroundColor: `${organizationColor}${TINT_ALPHA}`,
              borderColor: `${organizationColor}${RING_ALPHA}`,
            }
          : undefined
      }
    >
      {initial}
    </span>
  );
};

export const ProjectAvatar = memo(ProjectAvatarComponent);
